import {getSize, Icon, Show, Text} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import styled from 'styled-components';

import {isNotNil} from 'ramda';

import {TestDriveCalendarDrive} from '@dms/api/sales';

import {EMPTY_PLACEHOLDER, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {CALENDAR_LAYOUT} from '../constants/CALENDAR_LAYOUT';
import {TestDriveSegment} from '../utils/getVehicleDriveSegments';

interface TestDriveEventBarProps extends RequiredTestIdProps {
  segment: TestDriveSegment;
  leftPercent: number;
  widthPercent: number;
  isHovered: boolean;
  /** Faded while the pointer is on free space in the cell this drive starts in. */
  isDimmed: boolean;
  referenceRef: (element: HTMLElement | null) => void;
  onClick: (drive: TestDriveCalendarDrive) => void;
  onHoverStart: (segment: TestDriveSegment) => void;
  onHoverEnd: () => void;
}

const STATE_STYLE = {
  planned: {paletteKey: 'orange', icon: 'action/schedule'},
  completed: {paletteKey: 'green', icon: 'action/check_circle'},
} as const;

const DIMMED_OPACITY = 0.3;

// eslint-disable-next-line eag/no-css-property -- Muted pre-existing violation
const Positioner = styled.div<{$left: number; $width: number; $lane: number; $isDimmed: boolean}>`
  position: absolute;
  opacity: ${(props) => (props.$isDimmed ? DIMMED_OPACITY : 1)};
  transition: opacity 200ms ease;
  left: ${(props) => props.$left}%;
  width: ${(props) => props.$width}%;
  top: ${(props) => props.$lane * CALENDAR_LAYOUT.ROW_LANE_HEIGHT}px;
  height: ${CALENDAR_LAYOUT.ROW_LANE_HEIGHT - CALENDAR_LAYOUT.BAR_VERTICAL_GAP}px;
`;

// eslint-disable-next-line eag/no-css-property -- Muted pre-existing violation
const Bar = styled.div<{
  $paletteKey: (typeof STATE_STYLE)[keyof typeof STATE_STYLE]['paletteKey'];
  $isClickable: boolean;
}>`
  box-sizing: border-box;
  width: 100%;
  height: ${CALENDAR_LAYOUT.ROW_LANE_HEIGHT - CALENDAR_LAYOUT.BAR_VERTICAL_GAP}px;
  background-color: ${(props) => props.theme.colors.palettes[props.$paletteKey][60][100]};
  border-radius: ${(props) => props.theme.radii.small};
  padding: 0 ${getSize(1)};
  display: flex;
  align-items: center;
  gap: ${getSize(1)};
  overflow: hidden;
  cursor: ${(props) => (props.$isClickable ? 'pointer' : 'default')};
  user-select: none;
`;

export function TestDriveEventBar(props: TestDriveEventBarProps) {
  const formatDateTime = useDateTimeFormatter();

  const stateStyle = STATE_STYLE[props.segment.drive.state];

  const isClickable = isNotNil(props.segment.drive.businessCaseId);

  const timeRange = `${formatDateTime('timeShort', props.segment.actualStart)}–${formatDateTime('timeShort', props.segment.actualEnd)}`;
  const customerName = props.segment.drive.customerDisplayName ?? EMPTY_PLACEHOLDER;

  return (
    <Positioner
      $left={props.leftPercent}
      $width={props.widthPercent}
      $lane={props.segment.lane}
      $isDimmed={props.isDimmed}
    >
      <Bar
        ref={props.isHovered ? props.referenceRef : undefined}
        data-testid={suffixTestId('bar', props)}
        $paletteKey={stateStyle.paletteKey}
        $isClickable={isClickable}
        onClick={(event) => {
          // The row underneath opens the create dialog; a click on a bar is never also a new drive.
          event.stopPropagation();

          if (isClickable) {
            props.onClick(props.segment.drive);
          }
        }}
        onMouseEnter={() => props.onHoverStart(props.segment)}
        onMouseLeave={props.onHoverEnd}
      >
        <Show when={props.segment.isClampedStart}>
          <Icon value="navigation/chevron_left" size={4} color="text.white" />
        </Show>
        <Icon value={stateStyle.icon} size={4} color="text.white" />
        <Text size="xSmall" color="white" noWrap>
          {`${timeRange} · ${customerName}`}
        </Text>
        <Show when={props.segment.isClampedEnd}>
          <Icon value="navigation/chevron_right" size={4} color="text.white" />
        </Show>
      </Bar>
    </Positioner>
  );
}
